import { toast } from "vue3-toastify"

type AudioInstances = Record<string, HTMLAudioElement>
export interface audioOptions {
    loop?: boolean
    volume?: number,
    muted?: boolean
}

class AudioManager {
    private audios: AudioInstances = {}
    private defaultOptions: audioOptions = {
        loop: false,
        volume: 1,
        muted: false
    }

    initialize(src: string, key: string = crypto.randomUUID()): [string, HTMLAudioElement] {
        const existingAudio = this.audios[key]

        // Reuse or create new instance
        if (!existingAudio) {
            this.audios[key] = new Audio(src)
        }

        const audio = this.audios[key]

        if (audio.src !== src) {
            audio.src = src
        }

        // if (audio.currentTime) {
        //     audio.currentTime = 0
        // }

        return [key, audio];
    }

    // Play audio and return the audio instance
    play(src: string, key?: string, _options?: audioOptions): HTMLAudioElement {
        const audio = this.initialize(src, key)[1]; // get audio instance
        const options = {
            ...this.defaultOptions,
            ..._options
        }
        // handle options
        audio.loop = options.loop ? true : false
        audio.volume = options.volume || 1
        audio.muted = options.muted || false
        audio.play().catch((err) => {
            toast.error(`${err.message}: ${src}`);
            audio.src = '';
        });

        return audio;
    }

    getPlayingAudiosKeys(): string[] {
        return Object.keys(this.audios).filter((k) => this.audios[k].paused === false)
    }

    playShuffle(srcList: string[], key?: string, _options?: audioOptions) {
        const playedIndexes = new Set<number>();

        const playNext = () => {
            if (playedIndexes.size >= srcList.length) {
                if (_options?.loop) {
                    playedIndexes.clear(); // Reset if looping is enabled
                } else {
                    return; // Stop if no loop
                }
            }

            // Get a random index that hasn't been played yet
            let index: number;
            do {
                index = Math.floor(Math.random() * srcList.length);
            } while (playedIndexes.has(index));

            playedIndexes.add(index);

            // setting loop = false to trigger onended event for each playback to move on to the next audio
            const audio = this.play(srcList[index], key, { ..._options, loop: false })
            audio.onended = () => {
                index++
                playNext()
            }
        }

        playNext()
    }

    playIndex(srcList: string[], index: number, key?: string, _options?: audioOptions) {
        this.play(srcList[index], key, _options)
    }

    pause(key: string) {
        const audio = this.audios[key]
        if (audio) {
            audio.pause()
        }
        return audio
    }

    stop(key: string) {
        const audio = this.audios[key]
        if (audio) {
            audio.pause()
            audio.src = ''
            delete this.audios[key]
        }
        return audio
    }

    stopAll() {
        Object.keys(this.audios).forEach((key) => this.stop(key))
    }

    getVolume(key: string): audioOptions {
        const audio = this.audios[key]
        if (!audio) return this.defaultOptions
        return {
            loop: audio.loop,
            volume: audio.volume,
            muted: audio.muted
        }
    }

    setVolume(key: string, targetVolume: number, duration: number = 500) {
        const audio = this.audios[key];
        if (!audio) return;

        const startVolume = audio.volume;
        const volumeDifference = targetVolume - startVolume;
        const stepTime = 50; // Interval in milliseconds
        const steps = duration / stepTime;
        let currentStep = 0;

        const interval = setInterval(() => {
            currentStep++;
            const progress = currentStep / steps;
            audio.volume = startVolume + volumeDifference * progress;

            if (currentStep >= steps) {
                clearInterval(interval);
                audio.volume = targetVolume; // Ensure the final volume is set
            }
        }, stepTime);
    }

    setMuted(key: string, muted: boolean) {
        const audio = this.audios[key]
        if (audio) {
            audio.muted = muted
        }
    }

    isPlaying(key: string): boolean {
        const audio = this.audios[key]
        return audio && !audio.paused
    }
}

export const audioManager = new AudioManager()